import axios from "axios";
import { toast } from "sonner";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// Optional Bearer store for a future native client; web uses HttpOnly cookie.
let _bearer = null;
export function setBearer(token) { _bearer = token || null; }
export function getBearer() { return _bearer; }

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000,
});

api.interceptors.request.use((cfg) => {
  if (_bearer) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${_bearer}`;
  }
  // Automatic Idempotency-Key for POST create-style calls (opt-out via
  // `cfg.idempotent === false` or when the caller has set one already).
  // Keys are v4 uuids; server dedupes on (key, method, path) for 24h and
  // replays the recorded response.
  if ((cfg.method || "").toLowerCase() === "post" && cfg.idempotent !== false) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers["Idempotency-Key"] && !cfg.headers["idempotency-key"]) {
      cfg.headers["Idempotency-Key"] = _uuidv4();
    }
  }
  return cfg;
});

// Small internal uuid (avoids adding a dep just for the header).
function _uuidv4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Response interceptor — surface structured error codes cleanly, don't crash React on FastAPI 422s.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const code = (detail && typeof detail === "object") ? (detail.code || detail.error) : null;
    if (status === 401) {
      if (err && typeof err === "object") err._silent_401 = true;
    }
    if (status === 402 && code === "seat_limit_reached") {
      toast.error(detail.message || "Seat limit reached", { duration: 6000 });
    }
    if (status === 403 && code === "module_disabled") {
      toast.error(detail.message || "Module disabled");
    }
    if (status === 403 && code === "capability_denied") {
      toast.error(detail.message || "You don't have permission");
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
